import { Trans } from '@lingui/react/macro'
import {
  ActionIcon,
  BackgroundImage,
  Box,
  Overlay,
  rem,
  Stack,
  Text,
} from '@mantine/core'
import { formatEuros } from '@markku/utils'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

interface SeasonHeaderProps {
  backgroundImage: string
  title: string
  dateRange: string
  totalCost: number
  onPrevious?: () => void
  onNext?: () => void
}

export function SeasonHeader({
  backgroundImage,
  title,
  dateRange,
  totalCost,
  onPrevious,
  onNext,
}: SeasonHeaderProps) {
  return (
    <Box
      style={{
        position: 'relative',
        height: rem(140),
        marginBottom: rem(20),
      }}
    >
      <BackgroundImage
        src={backgroundImage}
        style={{
          height: '100%',
          borderRadius: 'var(--mantine-radius-lg)',
        }}
      >
        <Overlay
          gradient="linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%)"
          opacity={0.9}
          zIndex={1}
          style={{
            borderRadius: 'var(--mantine-radius-lg)',
          }}
        />

        {/* Left Navigation Button */}
        {onPrevious && (
          <ActionIcon
            variant="transparent"
            size="lg"
            radius="md"
            onClick={onPrevious}
            style={{
              position: 'absolute',
              left: rem(10),
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            }}
          >
            <IconChevronLeft size={20} color="white" stroke={1.5} />
          </ActionIcon>
        )}

        {/* Right Navigation Button */}
        {onNext && (
          <ActionIcon
            variant="transparent"
            size="lg"
            radius="md"
            onClick={onNext}
            style={{
              position: 'absolute',
              right: rem(10),
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            }}
          >
            <IconChevronRight size={20} color="white" stroke={1.5} />
          </ActionIcon>
        )}

        {/* Season Title and Info */}
        <Stack
          justify="center"
          align="center"
          style={{
            height: '100%',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Text
            size="xxl"
            fw={700}
            c="white"
            style={{
              fontSize: rem(28),
              textShadow: '0 3px 8px rgba(0,0,0,0.7)',
            }}
          >
            {title}
          </Text>
          <Text
            size="lg"
            c="white"
            fw={500}
            style={{
              textShadow: '0 2px 6px rgba(0,0,0,0.6)',
            }}
          >
            {dateRange}
          </Text>
          {totalCost > 0 && (
            <Text
              size="md"
              c="white"
              fw={600}
              style={{
                textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                marginTop: rem(4),
              }}
            >
              <Trans>Costs: {formatEuros(totalCost)}</Trans>
            </Text>
          )}
        </Stack>
      </BackgroundImage>
    </Box>
  )
}
